// File created by Reginald Sahil Chand on 27-03-2024.

import React, {useEffect, type ReactElement, useState} from 'react';

import {type TaskProps} from '../../../../../types/tasks/createTaskTypes';
import {MdDelete} from 'react-icons/md';
import {toast} from 'react-toastify';

/**
 * @author Reginald Sahil Chand
 * @email Developer.ReginaldSahilChand@gmail.com
 *
 * @description Render all tasks created by the user.
 * @version 0.1.0
 *
 * @returns {JSX Element} ALLTasks component
 */
export const AllTasks: React.FC = React.memo((): ReactElement => {
	const [tasks, setTasks] = useState<TaskProps[]>(() => {
		let storedTasks: string | undefined = localStorage.getItem('Tasks')!;

		if (storedTasks === undefined || storedTasks === 'undefined') {
			storedTasks = '';
			localStorage.setItem('Tasks', '');
		}

		return storedTasks ? (JSON.parse(storedTasks) as TaskProps[]) : [];
	});

	useEffect(() => {
		const handleStorageChange = () => {
			const storedTasks = localStorage.getItem('Tasks');
			if (storedTasks) {
				setTasks(JSON.parse(storedTasks) as TaskProps[]);
			}
		};

		window.addEventListener('storage', handleStorageChange);

		return () => {
			window.removeEventListener('storage', handleStorageChange);
		};
	}, []);

	/**
	 * TODO: Move this function to its own component.
	 */
	const handleClick = (taskIndex: number) => {
		const updatedTasks = tasks.filter((task, index) => index !== taskIndex);
		localStorage.setItem('Tasks', JSON.stringify(updatedTasks));
		setTasks(updatedTasks);

		toast.success('Successfully deleted task.', {
			position: 'top-right',
			autoClose: 5000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'dark',
		});
	};

	return (
		<div className="w-full h-full">
			<ul className="w-full h-full pt-5 pr-5 custom-grid-three-cols overflow-y-auto">
				{tasks?.length === 0 || tasks === undefined || tasks === null ? (
					<li className="h-max ml-5 p-5 relative rounded-lg bg-primaryBackground md:w-[410px]">
						<h2 className="text-2xl font-bold text-text">Dear User</h2>
						<p className="my-5 text-base leading-8 text-[white] md:text-xl">
							Currently, there are no tasks to display. You can begin by creating a new task to get
							started.
						</p>
						<p className="text-base text-text md:text-xl">Thank you.</p>
					</li>
				) : (
					tasks?.map((element: TaskProps, index: number) => (
						<li
							key={index}
							className="h-[350px] ml-5 mb-5 p-5 relative rounded-lg bg-secondaryBackground"
						>
							<h2 className="mb-5 text-xl font-bold text-text">
								{element.taskTitle ?? 'Not title found'}
							</h2>
							<p className="text-xl text-text">
								{element.taskDescription ?? 'No description found'}
							</p>

							<button
								type="button"
								title="Click ToDelete"
								aria-label="Delete"
								tabIndex={0}
								onClick={() => {
									handleClick(index);
								}}
								className="w-[50px] h-[50px] absolute bottom-5 right-5 flex items-center justify-center text-text rounded-lg bg-[#ef4444]"
							>
								<div className="scale-125">
									<MdDelete />
								</div>
							</button>
						</li>
					))
				)}
			</ul>
		</div>
	);
});

AllTasks.displayName = 'AllTasks';
